import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CompanyContext = createContext({
  companies: [],
  active: null,
  activeId: "",
  setActiveId: () => {},
  reload: () => {},
  loading: true,
});

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveIdState] = useState(localStorage.getItem("tg_company_id") || "");
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) {
      setCompanies([]);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/companies/my");
      setCompanies(r.data || []);
      // Pick active: stored if valid, "__all__" allowed if >1, else first, else empty
      let nextId = localStorage.getItem("tg_company_id");
      if (nextId === "__all__" && (r.data || []).length < 2) nextId = "";
      if (nextId && nextId !== "__all__" && !r.data.some((c) => c.id === nextId)) {
        nextId = "";
      }
      if (!nextId) {
        nextId = r.data[0]?.id || "";
      }
      if (nextId) {
        localStorage.setItem("tg_company_id", nextId);
      } else {
        localStorage.removeItem("tg_company_id");
      }
      setActiveIdState(nextId);
    } catch (e) {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setActiveId = useCallback((id) => {
    if (!id) return;
    localStorage.setItem("tg_company_id", id);
    setActiveIdState(id);
    // Force a hard reload so all queries refetch with new header
    window.location.reload();
  }, []);

  const active = activeId === "__all__" ? null : companies.find((c) => c.id === activeId) || null;

  return (
    <CompanyContext.Provider value={{ companies, active, activeId, setActiveId, reload, loading }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
